import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import BackPage from "../../components/button/BackPage";
import RegularContainer from "../../components/container/RegularContainer";
import InnerTabs from "../../components/tab/InnerTabs";
import { selectCurrentUser } from "../../features/authentication/services";
import { checkHasPermission } from "../../features/authentication/utils";
import { useAppSelector } from "../../hooks";
import type { ILinkTabProps } from "../../types/interfaces";
import { isProduction } from "../../utils";

const tabRoutes: ILinkTabProps[] = isProduction
    ? [
          { labelKey: "CH.Info", href: "character-info" },
          { labelKey: "CH.Voice_Set", href: "voice-setting" },
          { labelKey: "CH.ChatRoom", href: "chatroom-setting" },
          { labelKey: "CH.Chat_History", href: "character-chat-history" },
          { labelKey: "CH.Setting", href: "character-setting" },
          { labelKey: "CH.MCP_Tools", href: "mcp-setting" },
      ]
    : [
          { labelKey: "CH.Info", href: "character-info" },
          { labelKey: "CH.Voice_Set", href: "voice-setting" },
          { labelKey: "CH.ChatRoom", href: "chatroom-setting" },
          { labelKey: "CH.Chat_History", href: "character-chat-history" },
          { labelKey: "CH.Setting", href: "character-setting" },
          { labelKey: "CH.MCP_Tools", href: "mcp-setting" },
      ];

const CharacterTabs = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = location;
    const { projectId, characterId } = useParams();
    const currentUser = useAppSelector(selectCurrentUser);

    const [currentTab, setCurrentTab] = useState(0);

    const permissionRoutes = tabRoutes.filter((route) => {
        if (route.href !== "mcp-setting") return true;
        return checkHasPermission({ role: currentUser?.role, resource: "mcpSettings", action: "view" });
    });

    const handleChange = (newValue: number) => {
        setCurrentTab(newValue);
    };

    const pathnameSplit = location?.pathname?.split("/");
    const currentRoute = pathnameSplit[pathnameSplit?.length - 1] || "";

    const findIdx = permissionRoutes.findIndex((r) => {
        return r.href === currentRoute;
    });

    const backUrl = state ? `/projects/${state}` : "/projects";

    useEffect(() => {
        // if findIdx is not -1, means it's project dashboard index page, show current tab === Dashboard
        if (findIdx !== -1) {
            setCurrentTab(findIdx);
            return;
        }
        setCurrentTab(0);
    }, [findIdx, currentRoute]);

    return (
        <>
            <BackPage path={backUrl} />
            <RegularContainer sx={{ pb: 0 }}>
                <RegularContainer.Heading title={t("CH.Edit") || ""} />
                <RegularContainer.Content>
                    <InnerTabs
                        currentTab={currentTab}
                        handleChange={handleChange}
                        tabRoutes={permissionRoutes}
                        tabsOnClick={(tabRoute) => {
                            navigate(`/projects/${projectId}/characters/${characterId}/${tabRoute.href}`, {
                                state: projectId,
                            });
                        }}
                    />
                </RegularContainer.Content>
            </RegularContainer>
        </>
    );
};

export default CharacterTabs;
